import { Button, DropdownItem, HFlow, Icon, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import useSession from 'components/auth/useSession'
import { DropdownButton } from 'components/dropdown'
import { useErrorHandler } from 'components/error'
import { confirm } from 'components/modals/confirm'
import { usePrinter } from 'components/print'
import { PrinterType } from 'components/print/model'
import { useLoadAtestadoByIdLazyQuery } from 'graphql/hooks.generated'
import { CidadaoBasico, TipoAtestadoEnum } from 'graphql/types.generated'
import React, { useState } from 'react'
import Permissions from 'types/Permissions'

import AtestadoModel from '../components/AtestadoModel'
import { ImpressaoAtestado } from '../impressao/ImpressaoAtestado'
import { RegistroLista } from './AtestadosTable'
import AtestadosVisualizacaoModal from './AtestadosVisualizacaoModal'

export interface AtestadoTableButtonsProps {
  registro: RegistroLista
  cidadao: Partial<CidadaoBasico>
  atestadoRecente?: AtestadoModel

  remove(index: number): void
}

export default function AtestadoTableButtons({
  registro,
  cidadao,
  atestadoRecente,
  remove,
}: AtestadoTableButtonsProps) {
  const [isModalVisualizacaoOpen, setIsModalVisualizacaoOpen] = useState(false)

  const { printPDF } = usePrinter()
  const { hasAuthorization } = useSession({ fetchPolicy: 'cache-only' })
  const handleRejection = useErrorHandler()
  const alert = useAlert()

  const handleExcluir = () => {
    const isLicencaMaternidade = TipoAtestadoEnum.LICENCA_MATERNIDADE === atestadoRecente.tipo
    const titleConfirm = isLicencaMaternidade ? 'Deseja excluir a licença maternidade?' : 'Deseja excluir o atestado?'
    const alertSucess = isLicencaMaternidade
      ? 'Licença maternidade excluída com sucesso'
      : 'Atestado excluído com sucesso'
    return confirm({
      title: titleConfirm,
      type: 'danger',
      confirmLabel: 'Excluir',
      onConfirm: () => {
        remove(registro.id)
        alert('success', alertSucess)
      },
    })()
  }

  const hasPermissionImprimirAtestado = hasAuthorization(
    Permissions.visualizarProntuario.soap.plano.visualizarAtestados.imprimir
  )

  const [executeQuery, { loading }] = useLoadAtestadoByIdLazyQuery({
    onCompleted: (data) => {
      const infoProfissional: PrinterType = {
        acesso: data?.atestado.atendimentoProfissional.lotacao,
        profissional: data?.atestado.atendimentoProfissional.lotacao.profissional,
      }

      const docBody = ImpressaoAtestado({
        tipoAtestado: data?.atestado.tipo,
        content: data?.atestado.descricao,
        municipio: data?.atestado.atendimentoProfissional.lotacao.municipio,
        dataAtendimento: data?.atestado.atendimentoProfissional.iniciadoEm,
      })

      printPDF(
        {
          docBody,
          documentTitle: 'Atestado-' + (cidadao.nomeSocial || cidadao.nome),
          session: infoProfissional,
        },
        handleRejection
      )
    },
  })

  const handlePrint = () => {
    if (!registro.recente) {
      executeQuery({
        variables: { id: registro.id },
      })
    } else {
      const docBody = ImpressaoAtestado({
        tipoAtestado: atestadoRecente.tipo,
        content: atestadoRecente.descricao,
        municipio: registro.atendimentoProfissional.lotacao.municipio,
        dataAtendimento: registro.atendimentoProfissional.iniciadoEm,
      })

      printPDF(
        {
          docBody,
          documentTitle: 'Atestado-' + (cidadao.nomeSocial || cidadao.nome),
        },
        handleRejection
      )
    }
  }

  return (
    <>
      <HFlow>
        <Tooltip text='Visualizar'>
          <Button size='small' skin='ghost' onClick={() => setIsModalVisualizacaoOpen(true)}>
            <Icon icon='zoomOutline' />
          </Button>
        </Tooltip>

        {hasPermissionImprimirAtestado && (
          <Tooltip text='Imprimir'>
            <Button size='small' skin='ghost' onClick={handlePrint} loading={loading}>
              <Icon icon='printerOutline' />
            </Button>
          </Tooltip>
        )}
        <DropdownButton disabled={!registro.recente}>
          <DropdownItem onClick={handleExcluir} type='danger'>
            <HFlow alignItems='center' hSpacing={0.5}>
              <Icon icon='trashOutline' />
              Excluir
            </HFlow>
          </DropdownItem>
        </DropdownButton>
      </HFlow>

      {isModalVisualizacaoOpen && (
        <AtestadosVisualizacaoModal
          registro={registro}
          descricaoAtestadoRecente={atestadoRecente.descricao}
          tipoAtestadoRecente={atestadoRecente.tipo}
          handlePrint={handlePrint}
          handleModalClose={() => setIsModalVisualizacaoOpen(false)}
        />
      )}
    </>
  )
}
